import { useCallback, useEffect, useReducer, useRef } from 'react';
import { nanoid } from 'nanoid';
import { arrayMove, insertAtIndex, removeAtIndex } from '#lib/utils/array.js';
import { set } from 'lodash-es';
import toast from 'react-hot-toast';
import { isFunction } from '#lib/utils/function.js';
import { newId } from '#helpers/helpers.js';

const ACTIONS = {
    REPLACE_STATE: 'replace_state',
    ADD_DAY: 'add_day',
    ADD_PLAN_FROM_PLACE: 'add_plan_from_place',
    DELETE_DAY: 'delete_day',
    MOVE_DAY: 'move_day',
    ADD_PLAN: 'add_plan',
    ADD_EXTERNAL_FLIGHT: 'add_flight',
    DELETE_PLAN: 'delete_plan',
    MOVE_PLAN: 'move_plan',
    ADD_ROOM: 'add_room',
    DELETE_ROOM: 'delete_room',
    MOVE_GALLERY: 'move_gallery',
    EDIT: 'edit',
    USE_TEMPLATE: 'use_template'
};

const makeEmptyDay = (index) => ({
    plans: [],
    id: `day-${index}`
});
const makeEmptyTemplate = (dayIndex, id) => ({
    id: id,
    name: '',
    description: '',
    day: dayIndex,
    gallery: []
});
const makeEmptyActivity = (dayIndex, id) => ({
    id: id,
    name: '',
    description: '',
    duration: 0,
    type: 'activity',
    day: dayIndex,
    gallery: [],
    price: 0
});
const makeEmptyAccommodation = (dayIndex, id) => ({
    id: id,
    name: '',
    description: '',
    type: 'accommodation',
    checkIn: dayIndex,
    nights: 1,
    gallery: [],
    price: 0
});
const makeEmptyTransfer = (dayIndex, id) => ({
    id: id,
    name: '',
    description: '',
    type: 'transfer',
    day: dayIndex,
    pax: 1,
    gallery: [],
    price: 0
});
const makeEmptyFlight = (dayIndex, id) => ({
    id: id,
    description: '',
    type: 'transfer',
    day: dayIndex,
    pax: 1,
    name: _t.edit.planner['flight'](),
    transportation: 'F',
    gallery: [],
    price: 0
});
const makeEmptyCustom = (dayIndex, id, title, descr, gallery) => ({
    id: id,
    type: 'custom',
    day: dayIndex,
    gallery: gallery ?? [],
    name: title ?? '',
    description: descr ?? '',
    price: 0
});
const makeEmptyInfo = (dayIndex, id) => ({
    id: id,
    type: 'info',
    day: dayIndex,
    gallery: [],
    name: _t.edit.planner.info(),
    description: '',
    infoId: ''
});
const makeEmptySuggestion = (dayIndex, id) => ({
    id: id,
    type: 'suggestion',
    name: _t.edit.planner.suggestion(),
    suggestionType: '',
    previewMode: 'embed',
    defaultText: '',
    suggestionId: '',
    day: dayIndex
});
const makeInfo = (dayIndex, info) => ({
    id: info.id,
    type: 'info',
    day: dayIndex,
    gallery: [],
    name: info.name,
    description: info?.description,
    infoId: info.id
});
const makeSuggestion = (dayIndex, sugg) => ({
    id: sugg.id,
    type: 'suggestion',
    name: sugg.name,
    suggestionType: sugg.filterName,
    previewMode: 'embed',
    defaultText: _t.edit.planner.defaultTexts[sugg?.filterName]['embed'](),
    suggestionId: sugg.id,
    day: dayIndex
});

const makeEmptyRoom = (dayIndex, id, roomIndex) => ({
    id: id,
    day: dayIndex,
    name: _t.edit.planner['room']() + ` ${roomIndex + 1}`,
    gallery: []
});

const makeEmptyPlan = (type, dayIndex, id, title, descr) => {
    const planId = id ?? newId();
    switch (type) {
        case 'activity':
            return makeEmptyActivity(dayIndex, planId);
        case 'accommodation':
            return makeEmptyAccommodation(dayIndex, planId);
        case 'transfer':
            return makeEmptyTransfer(dayIndex, planId);
        case 'flight':
            return makeEmptyFlight(dayIndex, planId);
        case 'custom':
            return makeEmptyCustom(dayIndex, planId, title, descr);
        case 'suggestion':
            return makeEmptySuggestion(dayIndex, planId);
        case 'info':
            return makeEmptyInfo(dayIndex, planId);
        default:
            return makeEmptyTemplate(dayIndex, planId);
    }
};

const makeFlightFromApi = (model, dayIndex, id) => {
    if (!model || !model.hasOwnProperty('vehicleNumber') || !model.hasOwnProperty('company'))
        return makeEmptyFlight(dayIndex, id);
    const { arrival, departure, arrivalTime, departureTime, title, vehicleNumber, duration, company, flightKey } =
        model;
    return {
        id: id,
        day: dayIndex,
        name: title ?? _t.edit.planner['flight'](),
        description: duration ? _t.edit.flightDescription(duration) : '',
        gallery: [],
        arrival,
        departure,
        arrivalTime,
        departureTime,
        vehicleNumber,
        company,
        flightKey,
        type: 'transfer',
        transportation: 'F'
    };
};

function insertDay(days, index, dayItem) {
    const start = days.slice(0, index);
    const inserted = Array.isArray(dayItem) ? dayItem : [dayItem];
    const end = days.slice(index).map((day, dayIndex) => {
        day.plans.forEach((plan) => {
            plan.day += index;
        });
        day.id = `day-${index + dayIndex + 1}`;
        return day;
    });
    return start.concat(inserted, end);
}

function reducer(state, action) {
    switch (action.type) {
        case ACTIONS.REPLACE_STATE: {
            return [...action.payload];
        }
        case ACTIONS.ADD_DAY: {
            const index = action.payload ?? state.length;
            const newDays = insertDay(state, index, makeEmptyDay(index));
            return [...newDays];
        }
        case ACTIONS.DELETE_DAY: {
            const id = action.payload;
            const index = state.findIndex((day) => day.id === id);

            // se vogliamo eliminare il secondo giorno ma nel primo giorno c'è un piano di tipo accommodation allora non possiamo eliminarlo
            // perché non potremmo ridurre le notti dell'accommodation essendo il minimo 1, né spostare l'accommodation indietro

            if (index === 1 && !state[2]) {
                const accommodations = state[0].plans.filter((plan) => plan.type === 'accommodation');
                if (accommodations.length > 0) {
                    toast.error(_t.edit.planner.deleteSecondDayError());
                    return state;
                }
            }

            // se nei giorni precedenti ci sono delle accommodations le cui notti eccederebbero la lunghezza dell'itinerario, allora si riducono le notti delle accommodations
            // ma se c'è un accommodation con 1 notte nel penultimo giorno allora si sposta l'accommodation nel giorno precedente

            const newDays = removeAtIndex(state, index)[0];
            for (let i = 0; i < index; i++) {
                const accommodations = state[i].plans.filter((plan) => plan.type === 'accommodation');
                accommodations.forEach((accommodation) => {
                    if (Number(accommodation.nights) + 1 >= state.length - i) {
                        if (accommodation.nights > 1) accommodation.nights--;
                        else {
                            const accommodationIndex = newDays[i].plans.findIndex(
                                (plan) => plan.id === accommodation.id
                            );
                            const [updated, removed] = removeAtIndex(newDays[i]?.plans, accommodationIndex);
                            newDays[i].plans = updated;
                            newDays[i - 1].plans.push(removed);
                        }
                    }
                });
            }
            return [...newDays];
        }
        case ACTIONS.MOVE_DAY: {
            const { from, to } = action.payload;
            const newDays = arrayMove(state, from, to);
            return [...newDays];
        }
        case ACTIONS.ADD_PLAN_FROM_PLACE: {
            const { to, item, type, feedBack } = action.payload;
            const updated = structuredClone(state);
            const plan =
                type === 'suggestion'
                    ? makeSuggestion(to + 1, item)
                    : type === 'info'
                        ? makeInfo(to + 1, item)
                        : makeEmptyCustom(to + 1, nanoid(), item.name, item.description, item.gallery);
            const day = updated.at(to);
            const planIndex = day.plans?.length;
            day.plans = insertAtIndex(day.plans, planIndex ?? 0, plan);
            if (feedBack) toast.success(_t.edit.places.customPlanSuccess());
            return updated;
        }
        case ACTIONS.ADD_PLAN: {
            const { to, type, id, title, descr } = action.payload;
            const updated = structuredClone(state);
            const plan = makeEmptyPlan(type, to + 1, id, title, descr);
            if (Number.isNaN(to)) {
                return updated;
            }
            const day = updated.at(to);
            const planIndex = day.plans?.length;
            day.plans = insertAtIndex(day.plans, planIndex ?? 0, plan);
            return updated;
        }
        case ACTIONS.ADD_EXTERNAL_FLIGHT: {
            const { to, model, id } = action.payload;
            const updated = structuredClone(state);
            const plan = makeFlightFromApi(model, to + 1, id);
            const day = updated.at(to);
            const planIndex = day.plans?.length;
            day.plans = insertAtIndex(day.plans, planIndex ?? 0, plan);
            return updated;
        }
        case ACTIONS.DELETE_PLAN: {
            const { dayId, _planId } = action.payload;
            const updated = structuredClone(state);
            const day = updated.find((day) => day.id === dayId);
            const planIndex = day.plans.findIndex((plan) => plan.id === _planId);
            const newPlans = removeAtIndex(day.plans, planIndex);
            day.plans = newPlans[0];
            return updated;
        }
        case ACTIONS.MOVE_PLAN: {
            const { from, to } = action.payload;
            const [fromDayIndex, fromPlanIndex] = from;
            const [toDayIndex, toPlanIndex] = to;

            const updated = structuredClone(state);

            if (fromDayIndex === toDayIndex) {
                const day = updated.at(toDayIndex);
                day.plans = arrayMove(day.plans, fromPlanIndex, toPlanIndex);
                return updated;
            }

            const fromDay = updated.at(fromDayIndex);
            if (toDayIndex >= updated.length) {
                updated.push(...Array.from({ length: toDayIndex - updated.length + 1 }, (_, i) =>
                    makeEmptyDay(updated.length + i))
                );
            }
            const toDay = updated.at(toDayIndex);
            const [updatedFromDayPlans, removed] = removeAtIndex(fromDay.plans, fromPlanIndex);
            fromDay.plans = updatedFromDayPlans;
            toDay.plans = [...toDay.plans, removed];

            return updated;
        }
        case ACTIONS.ADD_ROOM: {
            const { to, id, roomId } = action.payload;
            const updated = structuredClone(state);
            if (Number.isNaN(to)) {
                return updated;
            }
            const day = updated.at(to);
            const plan = day.plans.find((plan) => plan.id === id);
            if (!plan?.rooms) {
                plan.rooms = [];
            }
            const room = makeEmptyRoom(to, roomId, plan?.rooms.length);
            plan.rooms = insertAtIndex(plan?.rooms, plan?.rooms.length, room);
            return updated;
        }
        case ACTIONS.DELETE_ROOM: {
            const { dayId, _planId, roomId } = action.payload;
            const updated = structuredClone(state);
            const day = updated.find((day) => day.id === dayId);
            const plan = day.plans.find((plan) => plan.id === _planId);
            const roomIndex = plan?.rooms.findIndex((room) => (room.id ?? room.index) === roomId);
            const newRooms = removeAtIndex(plan?.rooms, roomIndex);
            plan.rooms = newRooms[0];
            return updated;
        }
        case ACTIONS.EDIT: {
            const { dayId, planId, roomId, field, data } = action.payload;
            const updated = structuredClone(state);
            if (Number.isNaN(data)) return updated;
            if (!dayId) {
                // se viene effettuata una modifica senza avere l'id del giorno. Per esempio quando viene eliminato un documento dall'itinerario e va tolto anche dal piano in cui è stato associato
                const plans = updated.flatMap((day) => day.plans);
                const plan = plans.find((plan) => plan.id === planId);
                set(plan, field, data);
                return updated;
            }

            const dayIndex = dayId.split('-')[1];
            const day = updated[dayIndex];
            const plan = day.plans.find((plan) => plan.id === planId);

            if (roomId) {
                const room = plan?.rooms.find((room) => (room.id ?? room.index) === roomId);
                if (!room) {
                    return updated;
                }
                set(room, field, data);
                return updated;
            }
            set(plan, field, data);
            return updated;
        }
        case ACTIONS.MOVE_GALLERY: {
            const { at, from, to } = action.payload;
            const [dayId, planId, roomId] = at;
            const updated = structuredClone(state);
            const day = updated.find((day) => day.id === dayId);
            const plan = day.plans.find((plan) => plan.id === planId);
            if (roomId) {
                const room = plan.rooms.find((room) => (room.id ?? room.index) === roomId);
                room.gallery = arrayMove(room.gallery, from, to);
            } else plan.gallery = arrayMove(plan.gallery, from, to);
            return updated;
        }
        case ACTIONS.USE_TEMPLATE: {
            const { template, planId } = action.payload;
            const updated = structuredClone(state);
            let plan = updated.flatMap((day) => day.plans).find((plan) => plan.id === planId);
            if (template.type === 'accommodation' && plan) {
                const daysToAdd = plan.checkIn + template.nights - updated.length;
                if (daysToAdd > 0) {
                    for (let i = 0; i < daysToAdd; i++) {
                        updated.push(makeEmptyDay(updated.length));
                    }
                }
            }
            if (!plan) return updated;
            Object.assign(plan, template);
            plan.id = planId;
            return updated;
        }
    }
}

export function usePlanner({ data, init, setUnsavedChanges }) {
    const [state, dispatch] = useReducer(reducer, data, isFunction(init) ? init : () => data);

    useEffect(() => {
        if (data) {
            const days = isFunction(init) ? init(data) : structuredClone(data);
            replaceState(days);
        }
    }, [init, data]);

    const addDay = useCallback((index) => {
        dispatch({ type: ACTIONS.ADD_DAY, payload: index });
        setUnsavedChanges(true);
    }, []);

    const addFlight = useCallback((to, model) => {
        const id = newId();
        dispatch({ type: ACTIONS.ADD_EXTERNAL_FLIGHT, payload: { to, model, id } });
        setUnsavedChanges(true);
        return id;
    }, []);

    const addPlan = useCallback((to, type, title, descr, feedBack) => {
        const id = newId();
        dispatch({ type: ACTIONS.ADD_PLAN, payload: { to, type, id, title, descr, feedBack } });
        setUnsavedChanges(true);
        return id;
    }, []);

    const addPlanFromPlace = useCallback((to, item, type, feedBack) => {
        dispatch({ type: ACTIONS.ADD_PLAN_FROM_PLACE, payload: { to, item, type, feedBack } });
        setUnsavedChanges(true);
    }, []);

    const addRoom = useCallback((to, id, roomId) => {
        dispatch({ type: ACTIONS.ADD_ROOM, payload: { to, id, roomId } });
        setUnsavedChanges(true);
    }, []);

    const deleteDay = useCallback((index) => {
        dispatch({ type: ACTIONS.DELETE_DAY, payload: index });
        setUnsavedChanges(true);
    }, []);

    const deletePlan = useCallback((dayId, id) => {
        dispatch({
            type: ACTIONS.DELETE_PLAN,
            payload: { dayId, _planId: id }
        });
        setUnsavedChanges(true);
    }, []);

    const deleteRoom = useCallback((dayId, id, roomId) => {
        dispatch({
            type: ACTIONS.DELETE_ROOM,
            payload: { dayId, _planId: id, roomId }
        });
        setUnsavedChanges(true);
    }, []);

    const edit = useCallback((at, field, data, multiAction) => {
        const [dayId, planId, roomId] = at;
        if (multiAction) {
            for (const key in field) {
                dispatch({
                    type: ACTIONS.EDIT,
                    payload: { dayId, planId, roomId, field: key, data: field[key] }
                });
            }
        } else {
            dispatch({
                type: ACTIONS.EDIT,
                payload: { dayId, planId, roomId, field, data }
            });
        }
        setUnsavedChanges(true);
    }, []);

    const moveDay = useCallback((from, to) => {
        dispatch({ type: ACTIONS.MOVE_DAY, payload: { from, to } });
        setUnsavedChanges(true);
    }, []);

    const moveGallery = useCallback((at, from, to) => {
        dispatch({ type: ACTIONS.MOVE_GALLERY, payload: { at, from, to } });
        setUnsavedChanges(true);
    }, []);

    const movePlan = useCallback((from, to) => {
        dispatch({ type: ACTIONS.MOVE_PLAN, payload: { from, to } });
        setUnsavedChanges(true);
    }, []);

    const replaceState = useCallback((state) => {
        dispatch({ type: ACTIONS.REPLACE_STATE, payload: state });
    }, []);

    const useTemplate = useCallback((template, planId) => {
        dispatch({ type: ACTIONS.USE_TEMPLATE, payload: { template, planId } });
        setUnsavedChanges(true);
    }, []);

    return {
        days: state,
        replaceState,
        addDay,
        deleteDay,
        moveDay,
        addPlan,
        addFlight,
        addPlanFromPlace,
        deletePlan,
        movePlan,
        addRoom,
        deleteRoom,
        edit,
        moveGallery,
        useTemplate
    };
}
